import { CacheProvider } from "@emotion/react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { App } from "./App.js";
import { ErrorBoundary } from "./components/ErrorBoundary.js";
import { AuthProvider } from "./auth/AuthProvider.js";
import { installDesktopApiBase } from "./lib/desktopApiBase.js";
import { installPromiseWithResolvers } from "./lib/promiseWithResolvers.js";
import { muiCache } from "./lib/emotionCache.js";
import { tokenStore } from "./lib/tokenStore.js";
import { theme } from "./styles/theme.js";
import "./styles/globals.css";

// pdf.js needs Promise.withResolvers, which older webviews (and Cypress'
// Electron) lack — install it before anything imports the PDF reader, or the
// invoice/quote/documentation preview crashes into the error boundary.
installPromiseWithResolvers();

// Desktop app: re-point relative `/api/...` calls at the real server BEFORE
// anything fetches (the SPA loads from tauri://localhost, where relative
// URLs would hit the app bundle and fail — "could not bootstrap CRM").
// No-op in the browser (same-origin). Must run before tokenStore / render.
installDesktopApiBase();

// Synchronously mirror the dashboard's access token into the
// xop_crm_token localStorage key that the copied CRM source reads.
// Runs BEFORE React renders so the CRM's AuthGuard sees a populated
// token on its first useEffect tick.
void tokenStore.getAccess();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: (count, err) => {
        // Auth errors don't get retried — let the AuthProvider handle them.
        if (isAuthError(err)) return false;
        return count < 2;
      },
    },
  },
});

const root = document.getElementById("root");
if (!root) throw new Error("#root element missing from index.html");

createRoot(root).render(
  <StrictMode>
    <CacheProvider value={muiCache}>
      <ThemeProvider theme={theme}>
        {/* Applies the theme's background/typography baseline. `prepend` on the
            emotion cache keeps these below globals.css in the cascade during
            the Tailwind→MUI transition, so unconverted surfaces are untouched. */}
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          {/* Last-resort full-screen net: catches a render throw from the
              router / auth / shell themselves (the route-scoped boundary in
              AppShell handles per-page throws). Either way the user sees a
              real error surface, never a silent white screen. */}
          <ErrorBoundary variant="app">
            <BrowserRouter>
              <AuthProvider>
                <App />
              </AuthProvider>
            </BrowserRouter>
          </ErrorBoundary>
        </QueryClientProvider>
      </ThemeProvider>
    </CacheProvider>
  </StrictMode>,
);

function isAuthError(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    "code" in err &&
    typeof (err as { code: unknown }).code === "string" &&
    (err as { code: string }).code.startsWith("auth/")
  );
}
